// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Every user-facing string.
 *
 * Kept in one file so the honesty gate can walk all of it: a test asserts that nothing here claims
 * to know what is inside the system prompt or the tool schemas, because the log does not contain
 * them and a tool that implies otherwise is lying about the one thing it is for.
 */

export const TAGLINE = 'Where your agent run’s tokens actually went.';

export const HERO_TITLE = 'You are not billed once for each thing that happened.';

export const HERO_BODY =
  'Every turn re-sends the whole conversation. A big tool result added at turn 5 of a 200-turn run ' +
  'is not paid for once — it is paid for again on all 195 turns after it, at [[cache-read|cache-read]] prices. ' +
  'Drop a session file and runledger will show you what your context was carrying, turn by turn, ' +
  'priced from the numbers the session itself reported.';

export const TRUST_STRONG = 'Runs entirely in your browser.';

export const TRUST_BODY =
  ' A session log contains every file your agent read — effectively your whole codebase. It is ' +
  'parsed in this tab and never uploaded. A full run makes no network request at all.';

/** The panel heading. Deliberately different from the drop-zone title below it — the two sat one
 *  above the other reading identically, which looked like a rendering bug. */
export const DROP_HEADING = 'Your session files';

export const DROP_TITLE = 'Drop your session <code>.jsonl</code> files';

export const DROP_SUB =
  'One file or a hundred. On macOS and Linux they are in <code>~/.claude/projects/</code>; on ' +
  'Windows, <code>%USERPROFILE%\\.claude\\projects\\</code>. Hidden folders need ' +
  '<code>Cmd+Shift+.</code> in the macOS file dialog.';

export const FOLDER_NOTE =
  'Opening a folder is a desktop-Chromium convenience only, and browsers refuse to pick some ' +
  'sensitive locations. Dragging the files in always works.';

/** The three sections of the privacy modal. */
export const PRIVACY_PROTECTED = [
  'Every byte of every session file you drop. Parsing, hashing and pricing all happen in this tab.',
  'A complete run makes <strong>no network request at all</strong> — open your network inspector and watch.',
  'Nothing is written to disk. No IndexedDB, no Origin Private File System, no cookies. A build ' +
    'gate fails the release if any of those APIs appears in the shipped bundle.',
  'The only thing kept between visits is your theme choice and your edits to the price table.',
];

export const PRIVACY_NOT = [
  'The two files you choose to download contain token counts, dollar figures, tool names and short ' +
    'labels. They contain no line of your session — no file contents, no commands, no messages — ' +
    'and a test asserts that. But they do reveal <em>how</em> you worked, so read one before you ' +
    'attach it to a public ticket.',
  'runledger cannot tell you what is inside the system prompt, the tool schemas or your project ' +
    'instructions. <strong>None of that is in the log.</strong> It can measure how big that block ' +
    'is and what it cost you, and it refuses to guess at the contents.',
  'A cookie-less Cloudflare Web Analytics beacon records anonymous page views.',
];

export const PRIVACY_TRUST = [
  'The static bundle served by GitHub Pages, and the TLS chain between you and it.',
  'A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, ' +
    'no cross-site tracking; your files and data are never sent to it.',
  'Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you ' +
    'open the feedback form and press Send; your files and data never are.',
];

export const HOW_STEPS: { title: string; body: string }[] = [
  {
    title: '1 · Read the file without ever holding it whole',
    body:
      'Session files run to hundreds of megabytes. runledger streams the bytes, frames them into ' +
      'one JSON record per line inside a [[worker|Web Worker]], and discards each record once it ' +
      'has been counted. Only totals cross back to the page.',
  },
  {
    title: '2 · Group records into turns',
    body:
      'One request emits several records — a thinking block, a text block and each tool call are ' +
      'separate lines that all carry the <em>same</em> usage numbers. Measured on a real session: ' +
      '452 records, 83 actual requests. Adding up the usage per record would report a bill nearly ' +
      'three times the real one.',
  },
  {
    title: '3 · Check the arithmetic against the session itself',
    body:
      'Each turn’s cached prefix should be the previous turn’s prompt. Across 12,475 turn pairs ' +
      'from 80 real sessions that holds to a median of exactly 2 tokens, and within ±64 for 96.9% ' +
      'of them. Where it does not hold, the [[reset|cache was replaced]] — runledger marks the ' +
      'point rather than pricing through it.',
  },
  {
    title: '4 · Work out what each turn is still costing you',
    body:
      'What a turn adds is re-read on every later turn until the context is rebuilt. So each row ' +
      'is <em>tokens × the number of turns that re-read them</em>. No estimation and no ' +
      'tokenizer: the token counts are the ones the session reported.',
  },
  {
    title: '5 · Show its own working',
    body:
      'The decomposition should add back up to the reported cache-read total. runledger prints ' +
      'the percentage it actually accounts for — measured at 100.0% median across 40 real ' +
      'sessions — so you can see whether to believe the table above it.',
  },
];

export const ABOUT_BODY =
  'runledger opens an agent session log and answers one question: what was my context carrying, ' +
  'and what did carrying it cost? It reads Claude Code session files and prices them from the ' +
  'published rate card, using only the token counts the session itself recorded.';

/**
 * The limits strip. This is the part of the interface that says what the tool cannot do, and it is
 * on the page rather than buried in a modal because the honest answer to "which MCP server's
 * schema is costing me?" is that the log does not contain the schemas.
 */
export const HONESTY =
  'runledger reports what your session file recorded. It does not know what was in the system ' +
  'prompt, your project instructions or any tool schema, because none of that is written to the log.';

export const LIMITS = [
  'It can measure the block that sits in front of every turn, and price it exactly. It cannot ' +
    'break it down, and it will not pretend to.',
  'Repeats are reported in <strong>bytes</strong>, never in dollars. A turn appends several blocks ' +
    'under one exact token count, and splitting that count between them by size would be a guess: ' +
    'the bytes-per-token ratio measured across 40 real sessions ranges from 0.45 to 11.45.',
  'There is no near-duplicate detection. The obvious near-duplicate — the same file read again ' +
    'after an edit — is not waste, it is the job, and flagging it would be a false accusation.',
  'Prices come from a dated table you can edit. If the published rates have moved since the date ' +
    'shown, the totals move with whatever you type.',
  'Claude Code session files only. There is no OpenTelemetry, LangSmith or Langfuse importer.',
];

export const GLOSSARY: Record<string, { term: string; def: string }> = {
  'cache-read': {
    term: 'Cache read',
    def:
      'When a request repeats a prefix the model has already seen, that prefix is billed at a tenth ' +
      'of the normal input price instead of being reprocessed. Cheap per token — but it happens on ' +
      'every single turn, which is why it is usually the largest line on an agent bill.',
  },
  'cache-write': {
    term: 'Cache write',
    def:
      'Storing a prefix so later requests can read it back. It costs more than plain input — 1.25x ' +
      'for the five-minute store, 2x for the one-hour store — and it is paid again every time the ' +
      'cache is rebuilt.',
  },
  baseline: {
    term: 'Baseline',
    def:
      'Everything already in the context before the first reply: the system prompt, the tool ' +
      'schemas and your project instructions. runledger reads its exact size from the first turn’s ' +
      'own numbers. The contents are not in the log.',
  },
  carry: {
    term: 'Carry',
    def:
      'What a turn is still costing you after it is over. Content added at one turn is re-sent on ' +
      'every later turn, so its carry is its token count times the number of turns that re-read it.',
  },
  reset: {
    term: 'Context reset',
    def:
      'A point where the cached prefix stopped being the previous turn’s prompt — the cache expired, ' +
      'the model changed, or the conversation was rebuilt. The whole context is written again at ' +
      'cache-write prices, which makes a reset one of the most expensive single events in a run.',
  },
  worker: {
    term: 'Web Worker',
    def:
      'A background thread in the browser. Parsing a large file on the main thread would freeze the ' +
      'page; doing it in a worker keeps the interface responsive and keeps file contents off the ' +
      'page entirely.',
  },
  turn: {
    term: 'Turn',
    def:
      'One request to the model. A single turn usually writes several lines to the log — thinking, ' +
      'text, and one line per tool call — and all of them report the same token usage.',
  },
};
